import React from "react";
import { Button } from "primereact/button";
import * as XLSX from "xlsx";
import { getRawMaterialReportExport } from "../../services/RawMaterials/RawMaterialService";

interface ExportRawMaterialReportProps {
  selectedUnits: number[];
  selectedRawMaterials: number[] | null;
  disabled?: boolean;
}

interface RawMaterialReportDTO {
  posId: number;
  posCode: string;
  rawMaterialName: string;
  quantity: number;
  value: number;
}

const ExportRawMaterialReport: React.FC<ExportRawMaterialReportProps> = ({
  selectedUnits,
  selectedRawMaterials,
  disabled = false,
}) => {
  const exportToExcel = async () => {
    if (!selectedUnits.length) return;

    try {
      // ✅ SAME FILTER LOGIC AS TABLE
      const materialFilter =
        Array.isArray(selectedRawMaterials) && selectedRawMaterials.length > 0
          ? selectedRawMaterials
          : null;

      const response = await getRawMaterialReportExport({
        posIds: selectedUnits,
        rawMaterialIds: materialFilter, // ✅ FIXED
      });

      if (!Array.isArray(response) || response.length === 0) return;

      const processedData = response.map((item: RawMaterialReportDTO) => ({
        "Unit Name": getUnitLabel(item.posCode),
        "Raw Material Name": item.rawMaterialName,
        Quantity: item.quantity,
        "Value (₹)": item.value,
      }));

      // -----------------------------------------
      // EXCEL SHEET BUILD
      // -----------------------------------------
      const header = ["Unit Name", "Raw Material Name", "Quantity", "Value (₹)"];
      const rows = processedData.map((r) => [
        r["Unit Name"],
        r["Raw Material Name"],
        Number(r["Quantity"]),
        Number(r["Value (₹)"]),
      ]);

      const worksheet = XLSX.utils.aoa_to_sheet([header, ...rows]);

      // Format Quantity & Value columns
      rows.forEach((_, i) => {
        const rowIndex = i + 1;

        const qtyCell = XLSX.utils.encode_cell({ r: rowIndex, c: 2 });
        const valCell = XLSX.utils.encode_cell({ r: rowIndex, c: 3 });

        if (worksheet[qtyCell]) {
          worksheet[qtyCell].t = "n";
          worksheet[qtyCell].z = "#,##0.00";
        }

        if (worksheet[valCell]) {
          worksheet[valCell].t = "n";
          worksheet[valCell].z = '"₹"#,##0.00';
        }
      });

      worksheet["!cols"] = [
        { width: 30 },
        { width: 35 },
        { width: 15 },
        { width: 18 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Raw Material Report");

      XLSX.writeFile(workbook, "RawMaterialReport.xlsx");
    } catch (error) {
      console.error("Error exporting raw material report:", error);
    }
  };

  // -----------------------------------------
  // UNIT LABEL MAPPING
  // -----------------------------------------
  const getUnitLabel = (posCode: string) => {
    switch (posCode) {
      case "POS-003":
        return "Unit-1 KR Nagar";
      case "POS-004":
        return "Unit-2 Magadi Taluk";
      default:
        return posCode;
    }
  };

  return (
    <Button
      label="Export Report"
      icon="pi pi-file-excel"
      className="p-button-success"
      onClick={exportToExcel}
      disabled={disabled || !selectedUnits.length}
      tooltip={
        !selectedUnits.length
          ? "Select units to export"
          : "Export to Excel"
      }
      tooltipOptions={{ position: "top" }}
    />
  );
};

export default ExportRawMaterialReport;
